/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { useHistory } from "react-router-dom";

import { Avatar, Box, Divider, Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import classNames from "classnames";

import {
    ApplicationReducer,
    ApplicationState,
    SubscriptionsReducer,
    UserReducer
} from '../../data/interfaces';
import { deleteApplication, fetchAppKeys, fetchSubscriptions } from '../../data/operations';
import { renderSkeleton } from '../../data/utils';
import OutlineButton from "../../elements/OutlineButton";
import CreateApp from "../app/CreateApp";
import { ConfirmationDialog } from "./ConfirmationDialog/ConfirmationDialog";
import { PATH_SEPARATOR } from "../../data/constants";
import {
    trackApplicationInfoBarDelete,
    trackApplicationInfoBarEdit,
} from "../../util/app-insights";
import ShareApplication from '../app/ShareApplication';
import ShareIcon from '../../icons/Share';
import { ApplicationPermission } from '../../util/app-permissions';
import { AppPermission } from '../../data/emus';

const useStyles = makeStyles((theme) => ({
    root: {
        flexGrow: 1,
        margin: '10px 0 10px 40px',
        width: 'auto'
    },
    appIconImage: {
        margin: 'auto',
        marginLeft: 0,
        display: 'flex',
        maxWidth: '100%',
        maxHeight: '100%',
        width: 49,
        height: 49,
        minWidth: 49,
        minHeight: 49,
        ...theme.custom.elements.appInfoBar.appIconImage
    },
    appIconImageBg: {
        ...theme.custom.elements.appInfoBar.appIconImageBackgroundColor
    },
    gridText: {
        textAlign: 'left',
        maxWidth: 'fit-content',
        marginLeft: -theme.spacing(0.8)
    },
    appName: {
        height: '24px',
        width: 'auto',
        ...theme.custom.elements.appInfoBar.appNameStyles
    },
    appOwnerProvider: {
        height: '24px',
        width: 'auto',
        ...theme.custom.elements.appInfoBar.appOwnerStyles
    },
    gridStyles: {
        maxWidth: 'fit-content'
    },
    outlineBtn: {
        padding: theme.spacing(0, 1.7, 0, 2.8),
        marginLeft: theme.spacing(1),
        borderRadius: theme.spacing(2),
        backgroundSize: theme.spacing(2.5),
    },
    editBtn: {
        ...theme.custom.elements.appInfoBar.editButtonStyles,
        ...theme.custom.elements.appInfoBar.outlineButtonStyles
    },
    shareBtn: {
        '& .MuiButton-startIcon': {
            margin: `${theme.spacing(0.4)}px 0 0 -${theme.spacing(1)}px`
        },
        ...theme.custom.elements.appInfoBar.outlineButtonStyles
    },
    permissionBtn: {
        backgroundSize: theme.spacing(2),
        ...theme.custom.elements.appInfoBar.permissionButtonStyles
    },
    hoverBtn: {
        height: theme.spacing(4),
        paddingLeft: theme.spacing(2.8),
        paddingRight: theme.spacing(1.7),
        minWidth: 0,
        borderRadius: theme.spacing(2),
        marginLeft: theme.spacing(1),
        ...theme.custom.elements.appInfoBar.buttonHoverStyles
    },
    deleteBtn: {
        height: theme.spacing(4),
        width: theme.spacing(4),
        marginLeft: theme.spacing(1),
        backgroundSize: theme.spacing(2),
        ...theme.custom.elements.appInfoBar.deleteButtonStyles
    },
    divider: {
        marginLeft: theme.spacing(7),
        marginRight: theme.spacing(7),
        marginTop: -theme.spacing(0.8),
        ...theme.custom.elements.appInfoBar.divider
    }
}));

interface AppInfoBarProps {
    userReducer: UserReducer,
    applicationReducer: ApplicationReducer,
    subscriptionReducer: SubscriptionsReducer,
    dispatchFetchSubscriptionsList: (applicationId: string, organizationId: string, type: string) => void,
    dispatchDeleteApplication: (applicationId: string) => void
}

function AppInfoBar(props: AppInfoBarProps) {
    const classes = useStyles();
    const intl = useIntl();
    const history = useHistory();
    const {
        userReducer, applicationReducer, dispatchDeleteApplication
    } = props;
    const { selectedOrg, userInfo, user } = userReducer;
    const selectedOrgHandleName = selectedOrg.handle;
    const isApplicationLoading = applicationReducer.isLoading;
    const appList = applicationReducer?.applications?.list;
    const application = appList?.length > 0 ? appList[0] : undefined;
    const applicationId = application?.applicationId;
    const applicationName = application?.name;
    const [applicationEditFormOpen, setApplicationEditFormOpen] = useState(false);
    const [applicationShareFormOpen, setApplicationShareFormOpen] = useState(false);
    const [applicationDeleteFormOpen, setApplicationDeleteFormOpen] = useState(false);
    const [appDeletionError, setAppDeletionError] = React.useState("");

    const onDeleteButtonClick = () => {
        setApplicationDeleteFormOpen(true);
        trackApplicationInfoBarDelete(`${applicationId}`);
    };

    const onDeleteDialogCloseClick = () => {
        setApplicationDeleteFormOpen(false);
    };

    const onDeleteDialogOkClick = async () => {
        try {
            await dispatchDeleteApplication(applicationId as string);
            history.push(PATH_SEPARATOR + selectedOrgHandleName + '/applications');
        } catch (err) {
            setAppDeletionError(err.message + "\n" + err!.response!.data);
        }
    };

    const onEditButtonClick = () => {
        setApplicationEditFormOpen(true);
        trackApplicationInfoBarEdit(`${applicationId}`);
    }; 
    
    const onShareButtonClick = () => {
        setApplicationShareFormOpen(true);
        trackApplicationInfoBarEdit(`${applicationId}`);
    };

    const onDialogCloseClick = () => {
        setApplicationEditFormOpen(false);
        setApplicationShareFormOpen(false);
    };

    const noOwnerLabel = intl.formatMessage({
        id: 'application.infoBar.noOwnerLabel',
        defaultMessage: 'No owner available'
    });

    const editButtonLabel = intl.formatMessage({
        id: 'application.infoBar.editButton.label',
        defaultMessage: 'Edit'
    });
    
    const shareButtonLabel = intl.formatMessage({
        id: 'application.infoBar.shareButton.label',
        defaultMessage: 'Share'
    });

    const ownedByLabel = intl.formatMessage({
        id: 'application.infoBar.ownedByLabel',
        defaultMessage: 'by '
    });

    const confirmationDialogI18n = {
        title: intl.formatMessage({
            id: 'application.infoBar.deleteConfirmationDialog.title',
            defaultMessage: 'Delete Application'
        }),
        content: intl.formatMessage({
            id: 'application.infoBar.deleteConfirmationDialog.content',
            defaultMessage: 'Are you sure you want to delete application - ' + applicationName + ' ?'
        }),
        okayButtonLabel: intl.formatMessage({
            id: 'application.infoBar.deleteConfirmationDialog.okButtonLabel',
            defaultMessage: 'Delete'
        }),
        cancelButtonLabel: intl.formatMessage({
            id: 'application.infoBar.deleteConfirmationDialog.cancelButtonLabel',
            defaultMessage: 'Cancel'
        }),
        loadingText: intl.formatMessage({
            id: 'application.infoBar.deleteConfirmationDialog.loadingText',
            defaultMessage: 'Deleting...'
        })
    };

    return (
        <div>
            <Grid container={true} spacing={3} direction={"row"} justifyContent={"space-between"} className={classes.root}>
                {
                    isApplicationLoading ?
                        <Grid item={true} xs={12} lg={2} style={{ maxWidth: 'fit-content' }}>
                            {renderSkeleton("rect", 49, 49)}
                        </Grid>
                        :
                        <Grid item={true} xs={12} lg={2} className={classes.gridStyles}>
                            <Avatar
                                className={classes.appIconImage}
                                classes={{ img: classes.appIconImageBg }}
                                data-testid="thumbnail"
                            >
                                {applicationName?.substr(0, 1).toUpperCase()}
                            </Avatar>
                        </Grid>
                }
                <Grid item={true} sm={true}>
                    <Grid
                        item={true}
                        xs={9}
                        sm={9}
                        lg={7}
                        className={classes.gridText}
                        container={true}
                        direction="column"
                        style={{ paddingRight: '60px' }}
                    >
                        {
                            isApplicationLoading ?
                                <Typography className={classes.appName}>
                                    {renderSkeleton("rect", 100, 20)}
                                </Typography>
                                :
                                <Typography className={classes.appName}>
                                    {applicationName}
                                </Typography>
                        }
                        {
                            isApplicationLoading ?
                                <Typography className={classes.appOwnerProvider}>
                                    {renderSkeleton("rect", 100, 20)}
                                </Typography>
                                :
                                <Typography className={classes.appOwnerProvider}>
                                    {
                                        application?.ownerInfo ? `${ownedByLabel} ${ 
                                            application?.ownerInfo?.displayName || 
                                            application?.ownerInfo?.email || 
                                            application?.ownerInfo?.id
                                        }` : 
                                        application?.owner ? `${ownedByLabel} ${application?.owner}` : 
                                        <i> {noOwnerLabel} </i>}
                                </Typography>
                        }
                    </Grid>
                </Grid>
                <Grid item={true} xs={3} sm={3} lg={2} className={classes.gridStyles}>
                    <Box display="flex" flexDirection="row" mr={5} mt={1}>
                        { ApplicationPermission.hasPermission(AppPermission.SHARE, application, userInfo, user.allowedScopes) && 
                            <OutlineButton                    
                                onClick={onShareButtonClick}
                                className={classNames(classes.outlineBtn, classes.shareBtn)}
                                buttonLabel={shareButtonLabel}
                                startIcon={<ShareIcon />}
                                testId="appliation-share-btn"
                            />
                        }
                        { ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes) && 
                            <OutlineButton
                                onClick={onEditButtonClick}
                                className={classNames(classes.outlineBtn, classes.editBtn)}
                                buttonLabel={editButtonLabel}
                                testId="appliation-edit-btn"
                            />
                        }
                        { ApplicationPermission.hasPermission(AppPermission.WRITE, application, userInfo, user.allowedScopes) && 
                            <Paper
                                elevation={0}
                                onClick={onDeleteButtonClick}
                                className={classes.deleteBtn}
                                data-testid="application-delete-btn"
                            />
                        }
                    </Box>
                </Grid>
            </Grid>
            {applicationEditFormOpen &&
                <CreateApp
                    open={applicationEditFormOpen}
                    onClose={onDialogCloseClick}
                    isEdit={true}
                    application={application}
                    redirectToApplication={true}
                />
            }
            {applicationShareFormOpen && 
                <ShareApplication
                    open={applicationShareFormOpen}
                    onClose={onDialogCloseClick}
                    application={application}
                />
            }
            {applicationDeleteFormOpen &&
                <ConfirmationDialog
                    open={applicationDeleteFormOpen}
                    dialogTitle={confirmationDialogI18n.title}
                    dialogContentText={confirmationDialogI18n.content}
                    okayButtonLabel={confirmationDialogI18n.okayButtonLabel}
                    cancelButtonLabel={confirmationDialogI18n.cancelButtonLabel}
                    isActionProcessing={isApplicationLoading}
                    error={appDeletionError}
                    loadingActionHelperText={confirmationDialogI18n.loadingText}
                    onClose={onDeleteDialogCloseClick}
                    onCancel={onDeleteDialogCloseClick}
                    onOkay={onDeleteDialogOkClick}
                />
            }
            <Divider variant="middle" className={classes.divider} />
        </div>
    );
}

const mapDispatchToProps = {
    dispatchDeleteApplication: deleteApplication,
    dispatchFetchAppKeys: fetchAppKeys,
    dispatchFetchSubscriptionsList: fetchSubscriptions,
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        userReducer: state.userReducer,
        applicationReducer: state.applicationReducer,
        subscriptionReducer: state.subscriptionsReducer,
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(AppInfoBar);
